import React, { useState, useEffect } from 'react';
import styles from './HonorariosPagadosModal.module.css';
import Button from '../ui/button';
import Input from '../ui/input';
import { showErrorToast, showSuccessToast } from '../../utils/notifications';
import { MyIcons } from '../icons/my-icons';
import { honorariosPagados } from '../../services/apiService';
import ConfirmacionModal from './ConfirmacionModal';

/**
 * Modal para marcar honorarios como pagados
 * Props:
 * - isOpen: boolean (si el modal está visible)
 * - onClose: función para cerrar el modal
 */
export default function HonorariosPagadosModal({ 
  isOpen, 
  onClose
}) {
  const [numFactura, setNumFactura] = useState('');
  const [fechaPagado, setFechaPagado] = useState('');
  const [numFacturaAdmin, setNumFacturaAdmin] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estados para el modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  // Limpiar estados cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setNumFactura('');
      setFechaPagado('');
      setNumFacturaAdmin('');
      setShowConfirmModal(false);
      setConfirmData(null);
      setLoading(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    // Reset form
    setNumFactura('');
    setFechaPagado('');
    setNumFacturaAdmin('');
    setShowConfirmModal(false);
    setConfirmData(null);
    onClose();
  };

  const handleSubmit = async (e, confirmar = false) => {
    if (e) {
      e.preventDefault();
    }

    // Validaciones
    if (!numFactura.trim()) {
      showErrorToast('Por favor, ingresa al menos un número de factura');
      return;
    }

    if (!fechaPagado) {
      showErrorToast('Por favor, selecciona la fecha de pago');
      return;
    }

    try {
      setLoading(true);

      // Llamar a la API
      const numFacturaAdminValue = numFacturaAdmin.trim() || null;
      const response = await honorariosPagados(
        numFactura.trim(), 
        fechaPagado, 
        numFacturaAdminValue,
        confirmar
      );

      // Verificar si necesita confirmación
      if (response.needsConfirmation && !confirmar) {
        console.log('Response completa:', JSON.stringify(response, null, 2));
        
        // Extraer todos los campos de alerta dinámicamente (excepto needsConfirmation, message, facturas y total)
        const alertFields = {};
        Object.keys(response).forEach(key => {
          if (key !== 'needsConfirmation' && key !== 'message' && key !== 'facturas' && key !== 'total' && key !== 'totalComisiones') {
            if (Array.isArray(response[key]) && response[key].length > 0) {
              alertFields[key] = response[key];
            }
          }
        });
        
        console.log('Campos de alerta extraídos:', alertFields);
        
        // Agregar facturas formateadas para revisión (con comisión y porcentaje)
        const facturasFormateadas = response.facturas.map(f => 
          `${f.numFactura} - ${f.costeFinal}€ - ${f.importeHonorarios}€`
        );
        
        setConfirmData({
          message: response.message || 'Hay avisos antes de marcar honorarios como pagados. Clica en confirmar si quieres continuar.',
          alertFields: {
            ...alertFields,
            'Facturas a marcar honorarios como pagados': facturasFormateadas
          },
          totalComisiones: response.totalComisiones
        });
        setShowConfirmModal(true);
        setLoading(false);
        return;
      }

      showSuccessToast('Honorarios marcados como pagados correctamente');
      // No cerrar el modal automáticamente
      // Resetear solo los campos del formulario
      setNumFactura('');
      setFechaPagado('');
      setNumFacturaAdmin('');
      setShowConfirmModal(false);
      setConfirmData(null);
    } catch (error) {
      console.error('Error marking fees as paid:', error);
      
      // Verificar si el error es un 409 con needsConfirmation
      if (error.response && error.response.status === 409 && error.response.data) {
        const errorData = error.response.data;
        console.log('Error 409 - Response completa:', JSON.stringify(errorData, null, 2));
        
        if (errorData.needsConfirmation && !confirmar) {
          // Extraer todos los campos de alerta dinámicamente
          const alertFields = {};
          Object.keys(errorData).forEach(key => {
            if (key !== 'needsConfirmation' && key !== 'message' && key !== 'facturas' && key !== 'total' && key !== 'totalComisiones') {
              if (Array.isArray(errorData[key]) && errorData[key].length > 0) {
                alertFields[key] = errorData[key];
              }
            }
          });
          
          console.log('Campos de alerta extraídos del error 409:', alertFields);
          
          // Agregar facturas formateadas para revisión (con comisión y porcentaje)
          const facturasFormateadas = errorData.facturas ? errorData.facturas.map(f => 
            `${f.numFactura} - ${f.importeHonorarios}€ (${f.comisionPorcentaje}%)`
          ) : [];
          
          setConfirmData({
            message: errorData.message || 'Hay avisos antes de marcar honorarios como pagados. Clica en confirmar si quieres continuar.',
            alertFields: {
              ...alertFields,
              ...(facturasFormateadas.length > 0 && { 'Facturas a marcar honorarios como pagados': facturasFormateadas })
            },
            totalComisiones: errorData.totalComisiones
          });
          setShowConfirmModal(true);
          setLoading(false);
          return;
        }
      }
      
      showErrorToast(error.message || 'Error al marcar los honorarios como pagados');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    handleSubmit(null, true);
  };

  if (!isOpen) return null;

  console.log('[HonorariosPagadosModal] Renderizando, isOpen:', isOpen);

  return (
    <>
      <div className={styles.overlay} onClick={handleClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2 className={styles.title}>Honorarios Pagados</h2>
            <button
              className={styles.closeButton}
              onClick={handleClose}
              type="button"
            >
              <MyIcons name="close" width={20} height={20} color="var(--gray)" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Número(s) de Factura <span className={styles.required}>*</span>
              </label>
              <Input
                type="text"
                value={numFactura}
                onChange={(e) => setNumFactura(e.target.value)}
                placeholder="numFactura1, numFactura2, numFactura3"
                disabled={loading}
                required
              />
              <span className={styles.hint}>
                Puedes ingresar varios números de factura separados por comas
              </span>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Fecha Pagado <span className={styles.required}>*</span>
              </label>
              <Input
                type="date"
                value={fechaPagado}
                onChange={(e) => setFechaPagado(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                Número de Factura Administrador
              </label>
              <Input
                type="text"
                value={numFacturaAdmin}
                onChange={(e) => setNumFacturaAdmin(e.target.value)}
                placeholder="Ingresa el número de factura del administrador"
                disabled={loading}
              />
            </div>

            <div className={styles.buttonGroup}>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                CANCELAR
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'GUARDANDO...' : 'GUARDAR'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmacionModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setLoading(false);
        }}
        onConfirm={handleConfirm}
        message={confirmData?.message}
        alertFields={confirmData?.alertFields}
        numFactura={numFactura}
        fecha={fechaPagado}
        numFacturaAdmin={numFacturaAdmin}
        onNumFacturaChange={setNumFactura}
        onFechaChange={setFechaPagado}
        onNumFacturaAdminChange={setNumFacturaAdmin}
        loading={loading}
        showNumFacturaAdmin={true}
        totalComisiones={confirmData?.totalComisiones}
      />
    </>
  );
}
